import type React from "react";
import {
  type CtaClickProps,
  type ITrackingContext as ITeamschemeTrackingContext,
  TrackingContext as TeamschemeTrackingContext,
  type TrackEventProps,
  type TrackingValue,
} from "@naf/teamscheme";
import type {
  AccidentReportViewModel,
  VehicleKey,
} from "#accident-statement/schema";
import { normalizeComponentTree } from "../shared/search/normalizeComponentTree";
import { anonymizeAbsoluteUrl, anonymizeLocation } from "./anonymizeLocation";
import { toUtmCampaign, type ReportLinkSource } from "./ReportLinkSource";
import { type ITrackingContext, TrackingContext } from "./TrackingContext";

const ADMIN_PATH = "/admin";

/*
  Admin pages must never reach GA: their urls carry real report ids and
  lookup tokens. AdminWrapper also installs no-op tracking contexts, but the
  router renders admin's HydrateFallback outside that tree, so the rule is
  enforced here too.
*/
function isTrackedPathname(pathname: string) {
  return pathname !== ADMIN_PATH && !pathname.startsWith(`${ADMIN_PATH}/`);
}

function push(payload: Record<string, unknown>) {
  if (!isTrackedPathname(window.location.pathname)) return;

  window.dataLayer.push(payload);

  if (import.meta.env.DEV) console.log("[tracking]", payload);
}

export function AppTrackingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  function trackCtaClick({ navigate, label }: CtaClickProps) {
    const location = window.location;

    let destination: string | undefined = undefined;
    if (typeof navigate === "string") {
      destination = navigate;
    } else if (navigate) {
      destination = navigate.pathname;
    }

    let absoluteDestinationPath: string | undefined = undefined;
    if (destination) {
      if (destination.startsWith("http")) {
        absoluteDestinationPath = destination;
      } else if (!destination.startsWith("/")) {
        /*
          If destination does not start with the http-protocol we must assume we
          are on our own domain, so hostname of target must be origin of where we are.
        */
        absoluteDestinationPath = `${location.origin}/${destination}`;
      } else {
        absoluteDestinationPath = location.origin + destination;
      }
    }

    push({
      event: "cta_click",
      event_params: {
        cta_location: anonymizeLocation(location),
        cta_text: normalizeComponentTree(label),
        cta_hostname: location.origin,
        cta_url: absoluteDestinationPath
          ? anonymizeAbsoluteUrl(absoluteDestinationPath)
          : undefined,
      },
    });
  }

  function trackEvent({
    category,
    action,
    label,
    value,
    ...params
  }: TrackEventProps) {
    push({ event: "event", category, action, label, value, ...params });
  }

  function trackPageView({ title, path }: { title: string; path: string }) {
    push({
      event: "VirtualPageView",
      virtualPageURL: path,
      virtualPageTitle: title,
    });
  }

  function trackReportLoad(report: AccidentReportViewModel, party: VehicleKey) {
    push({ event: "report", id: report.id, party });
  }

  function trackExperiment(experimentName: string) {
    push({ event: "experiment", experimentName });
  }

  function trackAppLoad() {
    push({
      event: "load",
      appVersion: import.meta.env.PUBLIC_ENV__REACT_APP_VERSION,
    });
  }

  function addTrackingContext(contextObject: {
    [attribute: string]: TrackingValue;
  }) {
    for (const [attribute, value] of Object.entries(contextObject)) {
      push({ [attribute]: value });
    }
  }

  function trackReportLinkOpened(source: ReportLinkSource) {
    push({ event: "contract_link_opened", campaign: toUtmCampaign(source) });
  }

  const trackingContext: ITeamschemeTrackingContext = {
    trackEvent,
    trackCtaClick,
  };
  const appTrackingContext: ITrackingContext = {
    event: trackEvent,
    pageView: trackPageView,
    appLoad: trackAppLoad,
    reportLoad: trackReportLoad,
    experiment: trackExperiment,
    context: addTrackingContext,
    reportLinkOpened: trackReportLinkOpened,
  };

  return (
    <TeamschemeTrackingContext.Provider value={trackingContext}>
      <TrackingContext.Provider value={appTrackingContext}>
        {children}
      </TrackingContext.Provider>
    </TeamschemeTrackingContext.Provider>
  );
}
